// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
} from '@/location/utilities/get-effective-third-party';
import { Stack, Typography, TypographyProps } from '@mui/material';

interface LocationForName {
  name: string;
  type?: 'SITE' | 'ZONE';
  zoneCount?: number;
  address?: { formattedAddress: string | null } | null;
  parentLocation?: {
    name: string;
    address?: { formattedAddress: string | null } | null;
  } | null;
}

interface LocationNameProps extends Omit<TypographyProps, 'children'> {
  location: LocationForName;
  showAddress?: 'tooltip' | 'subtitle' | false;
}

/**
 * Unified location name renderer. Formats zone-aware names and optionally
 * displays the effective address as a tooltip or subtitle.
 */
export function LocationName(props: LocationNameProps) {
  const { location, showAddress = false, ...typographyProps } = props;
  const displayName = getEffectiveLocationName(location);
  const address = getEffectiveAddress(location);

  if (showAddress === 'tooltip') {
    return (
      <ConditionalTooltip arrow tooltipText={address ?? undefined}>
        <Typography
          {...typographyProps}
          sx={{
            ...(address
              ? {
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                }
              : undefined),
            ...((typographyProps.sx ?? {}) as Record<string, unknown>),
          }}
        >
          {displayName}
        </Typography>
      </ConditionalTooltip>
    );
  }

  if (showAddress === 'subtitle') {
    return (
      <Stack>
        <Typography {...typographyProps}>{displayName}</Typography>
        {address && (
          <Typography variant="body2" color="text.secondary">
            {address}
          </Typography>
        )}
      </Stack>
    );
  }

  return <Typography {...typographyProps}>{displayName}</Typography>;
}
